import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import clsx from "clsx";

import classes from "./styles.module.scss";

import type { IAppointmentCommentProps } from "@components/organisms/appointmentCard";
import { DoctorInformation } from "@components/listBoxes/doctorInformation";
import { Grid } from "@components/atoms/Grid";
import {
  usePostCommentAppointment,
  useShowAppointment,
} from "@api/appointment";
import { BaseLoader } from "@components/molecules/baseLoader";
import { Typography } from "@components/atoms/typography";
import { RadioButton } from "@components/formControls/radioButton";
import type { IAppointmentCommentValues } from "@components/organisms/appointmentCard/components/appointmentComment/schema";
import {
  AppointmentCommentInitialValues,
  AppointmentCommentSchema,
} from "@components/organisms/appointmentCard/components/appointmentComment/schema";
import { FastTextarea } from "@components/fastFields";
import { Button } from "@components/atoms/button";
import { useNotifyPromise } from "@hooks";
import { closeAlert } from "@/stores/uiStore";

export function AppointmentComment({ id }: IAppointmentCommentProps) {
  const { t } = useTranslation("form");

  const notifyMutate = useNotifyPromise();
  const { mutateAsync, isLoading: isSubmitting } = usePostCommentAppointment();

  const formik = useFormik<IAppointmentCommentValues>({
    initialValues: AppointmentCommentInitialValues,
    validationSchema: AppointmentCommentSchema,
    onSubmit: (values) => {
      notifyMutate(
        mutateAsync({ id, comment: values.comment, score: values.score }),
      )
        .then(() => closeAlert())
        .catch(() => {});
    },
  });

  const { data, isLoading, isError } = useShowAppointment({
    variables: {
      appointmentId: id,
    },
  });

  const doctor = data?.data?.data?.doctor;

  return (
    <div className={clsx(classes.appointmentCard)}>
      <BaseLoader
        type={"general"}
        isLoading={isLoading}
        isError={isError}
        style={{ minHeight: "30vh" }}
      >
        <DoctorInformation
          isError={false}
          isLoading={false}
          data={{
            id: doctor?.id || "",
            userID: doctor?.user_id || "",
            firstName: doctor?.first_name || "",
            lastName: doctor?.last_name || "",
            avatar: doctor?.avatar?.url,
            city: doctor?.city?.value,
            education: doctor?.education?.value,
            gender: doctor?.gender?.value || "",
            speciality: doctor?.speciality?.value || "",
          }}
        />
        <Grid
          subtitle={"submit_comment"}
          noMarginBottom
          border={"top-gradient"}
        >
          <Grid.FullWidthColumn>
            <div className={clsx(classes.appointmentCard__question)}>
              <Typography fontWeight={"semi-bold"}>{t("star_q")}</Typography>
              <div className={clsx(classes.appointmentCard__radios)}>
                <RadioButton
                  selectedValue={formik.values.score}
                  value={"1"}
                  label={t("star_1")}
                  onChange={formik.handleChange("score")}
                />
                <RadioButton
                  selectedValue={formik.values.score}
                  value={"2"}
                  label={t("star_2")}
                  onChange={formik.handleChange("score")}
                />
                <RadioButton
                  selectedValue={formik.values.score}
                  value={"3"}
                  label={t("star_3")}
                  onChange={formik.handleChange("score")}
                />
                <RadioButton
                  selectedValue={formik.values.score}
                  value={"4"}
                  label={t("star_4")}
                  onChange={formik.handleChange("score")}
                />
                <RadioButton
                  selectedValue={formik.values.score}
                  value={"5"}
                  label={t("star_5")}
                  onChange={formik.handleChange("score")}
                />
              </div>
            </div>
          </Grid.FullWidthColumn>
          <Grid.FullWidthColumn>
            <FastTextarea name={"comment"} type={"comment"} formik={formik} />
          </Grid.FullWidthColumn>
          <Grid.FullWidthColumn>
            <div className={clsx(classes.buttons)}>
              <Button
                mode={"secondary"}
                label={t("comment_later")}
                onClick={() => closeAlert()}
              />
              <Button
                mode={"main"}
                label={t("submit_comment")}
                onClick={() => formik.submitForm()}
                isLoading={isSubmitting}
              />
            </div>
          </Grid.FullWidthColumn>
        </Grid>
      </BaseLoader>
    </div>
  );
}
